import React, { useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '@qapline/api';
import { formatNumber, timestamp } from '@qapline/helpers';
import { FormError, FormField } from '@qapline/ui';
import { AlertFilledIcon } from 'assets/icons';
import classNames from 'classnames';
import { InvoiceFormSchema, invoicePaymentStatusEnum } from 'modules/invoicing';
import { Checkbox } from 'modules/ui/components/new/Checkbox';
import { Pill } from 'modules/ui/components/new/Pill';
import { Typography } from 'modules/ui/components/new/Typography';

import styles from './PaidState.module.scss';

export interface IPaidStateProps {}

export const PaidState: React.FC<IPaidStateProps> = () => {
    const { t, i18n } = useTranslation();
    const { paidStateChangedBy, paymentConfirmationDate, paymentStatus, paidAmount, currency, paid } =
        useWatch<InvoiceFormSchema>();
    const { setValue } = useFormContext<InvoiceFormSchema>();
    const { user } = useAuthenticated();

    const processPaidStateChangeForPaidInvoice = (newPaidValue: boolean) => {
        // We want to track which user changed the paid field to false
        if (newPaidValue === true) {
            setValue('paidStateChangedBy_id', null);
            setValue('paidStateChangedBy', null);
            return;
        }
        if (newPaidValue === false) {
            if (!user) return;
            setValue('paidStateChangedBy_id', user.userId);
            setValue('paidStateChangedBy', {
                email: user.email,
                name: user.name,
                surname: user.surname,
                user_id: user.userId,
            });
            return;
        }
        return;
    };

    const processPaidStateChangeForUnpaidInvoice = (newPaidValue: boolean) => {
        // We want to track which user changed the paid field to true
        if (newPaidValue === true) {
            if (!user) return;
            setValue('paidStateChangedBy_id', user.userId);
            setValue('paidStateChangedBy', {
                email: user.email,
                name: user.name,
                surname: user.surname,
                user_id: user.userId,
            });
            return;
        }
        if (newPaidValue === false) {
            setValue('paidStateChangedBy_id', null);
            setValue('paidStateChangedBy', null);
            return;
        }
    };

    const handlePaidStateChange = (newPaidValue: boolean) => {
        // strategy for PAID and OVERPAID status
        if (
            paymentStatus === invoicePaymentStatusEnum.enum.PAID ||
            paymentStatus === invoicePaymentStatusEnum.enum.OVERPAID
        ) {
            return processPaidStateChangeForPaidInvoice(newPaidValue);
        }
        // strategy for UNDERPAID and UNPAID status
        if (
            paymentStatus === invoicePaymentStatusEnum.enum.UNDERPAID ||
            paymentStatus === invoicePaymentStatusEnum.enum.UNPAID
        ) {
            return processPaidStateChangeForUnpaidInvoice(newPaidValue);
        }
    };

    const getPaidLabel = () => {
        if (paymentStatus === invoicePaymentStatusEnum.enum.UNDERPAID) {
            return t('invoicing.form.invoiceStatus.underPaid');
        }
        if (paymentStatus === invoicePaymentStatusEnum.enum.OVERPAID) {
            return t('invoicing.form.invoiceStatus.overPaid');
        }
        return t('invoicing.form.invoiceStatus.paid');
    };

    const getPaidSubLabel = useCallback(() => {
        if (paymentStatus === invoicePaymentStatusEnum.enum.PAID && paymentConfirmationDate) {
            return t('invoicing.form.invoiceStatus.confirmedByBank', {
                date: timestamp(paymentConfirmationDate)?.format('DD. MM. YYYY'),
            });
        }
        if (
            (paymentStatus === invoicePaymentStatusEnum.enum.UNDERPAID ||
                paymentStatus === invoicePaymentStatusEnum.enum.OVERPAID) &&
            paymentConfirmationDate &&
            currency
        ) {
            return t('invoicing.form.invoiceStatus.amountReceivedByBank', {
                amount: formatNumber(Number(paidAmount), i18n, { currency: currency }),
                date: timestamp(paymentConfirmationDate)?.format('DD. MM. YYYY'),
            });
        }
        if (paymentStatus === invoicePaymentStatusEnum.enum.UNPAID && paid === true) {
            return t('invoicing.form.invoiceStatus.confirmedByUser');
        }
        return null;
    }, [currency, paid, paidAmount, paymentConfirmationDate, paymentStatus, t]);

    return (
        <Controller
            name="paid"
            render={({ field: { onChange, value, ...fieldProps }, fieldState: { error } }) => (
                <FormField errorSlot={error && <FormError>{error?.message}</FormError>}>
                    <label
                        className={classNames(styles.wrapper, {
                            [styles['wrapper-error']]: !!error,
                        })}
                    >
                        <Checkbox
                            error={!!error}
                            onCheckedChange={(value) => {
                                if (value === 'indeterminate') return;
                                onChange(value);
                                handlePaidStateChange(value);
                            }}
                            checked={value}
                            {...fieldProps}
                        />
                        <div className={styles.labelWrapper}>
                            <Typography>{getPaidLabel()}</Typography>
                            <Typography variant="base12" fontWeight="normal" color="secondary">
                                {getPaidSubLabel()}
                            </Typography>
                        </div>
                        <div className={styles.userWrapper}>
                            {(paymentStatus === invoicePaymentStatusEnum.enum.OVERPAID ||
                                paymentStatus === invoicePaymentStatusEnum.enum.UNDERPAID) &&
                                !paidStateChangedBy && (
                                    <Pill
                                        color="warning"
                                        colorMode="hard"
                                        size="medium"
                                        prefix={<AlertFilledIcon height={16} width={16} />}
                                    >
                                        <Typography variant="base14" fontWeight="medium" color="white">
                                            {t('invoicing.form.invoiceStatus.amountDoesNotMatch')}
                                        </Typography>
                                    </Pill>
                                )}
                            {paidStateChangedBy && (
                                <>
                                    <Pill color="primary" colorMode="hard" size="medium">
                                        <Typography variant="base14" fontWeight="medium" color="white">
                                            {t('invoicing.form.invoiceStatus.paidStateChangedBy', {
                                                name: `${paidStateChangedBy?.name} ${paidStateChangedBy?.surname}`,
                                            })}
                                        </Typography>
                                    </Pill>
                                </>
                            )}
                        </div>
                    </label>
                </FormField>
            )}
        />
    );
};
